import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import AddClientModal from '../components/AddClientModal';
import { savePrimaryContact, splitName } from '../utils/clientPrimaryContact';


const GENERAL_INTAKE_TOKEN = 'general-intake-8af401896f4a09ec16df6a5f';

export default function AdminPanel({ darkMode, staffUser }) {
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [primaryByClient, setPrimaryByClient] = useState(() => new Map());   // client id → its primary contact (the client screen's)
  const [portalUsers, setPortalUsers] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [editingClient, setEditingClient] = useState(null);   // the row whose Edit Client modal is open
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const [yearConfig, setYearConfig] = useState(null);
  const [addingYear, setAddingYear] = useState(false);
  const [bulkPreview, setBulkPreview] = useState([]);
  // Parcel year-roll (state-scoped) — separate from the folder Jan-1 auto-roll above.
  const [rollStatesSummary, setRollStatesSummary] = useState([]); // [{ state, parcel_count, max_year }]
  const [rollTargetYear, setRollTargetYear] = useState(String(new Date().getFullYear() + 1));
  const [rollStates, setRollStates] = useState(() => new Set()); // selected states to roll
  const [rollPreview, setRollPreview] = useState(null); // { parcels_to_create, parcels_to_skip, total_in_states }
  const [rollResult, setRollResult] = useState(null);   // { parcels_created, parcels_skipped, collector_rows_created }
  const [rolling, setRolling] = useState(false);

  const isDark = darkMode;

  const c = isDark ? {
    bg: 'var(--ink)', card: 'rgba(47,56,84,0.6)', border: '#374060',
    text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc',
    input: 'var(--ink)', inputBorder: '#434C68', th: 'rgba(26,34,56,0.5)',
    rowHover: 'rgba(255,255,255,0.03)',
  } : {
    bg: 'var(--bg)', card: '#ffffff', border: 'var(--line)',
    text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068',
    input: '#ffffff', inputBorder: '#c8ccd8', th: 'var(--line-soft)',
    rowHover: 'rgba(0,0,0,0.02)',
  };

  const s = {
    card: { background: c.card, border: `1px solid ${c.border}`, borderRadius: '10px', overflow: 'hidden', marginBottom: '16px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardHeader: { padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    cardTitle: { fontSize: '13px', fontWeight: '700', color: c.text },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '8px 14px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', background: c.th },
    td: { padding: '10px 14px', borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}`, fontSize: '13px', color: c.text, verticalAlign: 'middle' },
    input: { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', fontFamily: "'Public Sans',system-ui,sans-serif", boxSizing: 'border-box' },
    select: { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', fontFamily: "'Public Sans',system-ui,sans-serif" },
    textarea: { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', fontFamily: "'Public Sans',system-ui,sans-serif", resize: 'vertical', minHeight: '80px', boxSizing: 'border-box' },
    btn: { padding: '7px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding: '7px 14px', background: 'transparent', border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.sub, fontSize: '12px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnDanger: { padding: '7px 14px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '7px', color: '#f87171', fontSize: '12px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    badge: (color) => ({ background: `rgba(${color},0.12)`, color: `rgb(${color})`, borderRadius: '20px', padding: '3px 9px', fontSize: '11px', fontWeight: '600' }),
  };

  const loadClients = useCallback(async () => {
    const [{ data }, { data: prim, error: pErr }] = await Promise.all([
      supabase.from('clients').select('*').order('name'),
      // The primary contact is the client screen's record (utils/clientPrimaryContact), not clients.primary_contact_*.
      supabase.from('client_contact_roles').select('client_id, contact:client_contacts(first_name, last_name, email, phone)').eq('role', 'primary'),
    ]);
    if (pErr) console.warn('[admin] primary contacts read failed:', pErr.message);
    setClients(data || []);
    setPrimaryByClient(new Map((prim || []).map(r => [r.client_id, r.contact])));
  }, []);


  const loadPortalUsers = useCallback(async () => {
    const { data } = await supabase.from('client_portal_users').select('*, clients(name)').order('name');
    setPortalUsers(data || []);
  }, []);

  const loadYearConfig = useCallback(async () => {
    const { data } = await supabase.from('folder_year_config').select('*').single();
    setYearConfig(data);
  }, []);

  useEffect(() => {
    loadClients();
    loadPortalUsers();
    loadYearConfig();
  }, [loadClients, loadPortalUsers, loadYearConfig]);

  // Ensure the permanent general-intake token has a backing upload_tokens row (it's a hardcoded constant, so a DB
  // wipe would orphan it → "Invalid Link"). Select-then-insert only if missing — never overwrites (won't touch
  // files_received) and can't create a duplicate that would break ClientUpload's .maybeSingle().
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('upload_tokens').select('id').eq('token', GENERAL_INTAKE_TOKEN).maybeSingle();
      if (data) return;
      const { error } = await supabase.from('upload_tokens').insert({
        token: GENERAL_INTAKE_TOKEN, active: true, client_id: null, client_name: 'General Inbox', property_id: null,
        expires_at: null, files_received: 0, created_by: 'system',
        note: 'General intake — permanent multi-use link (do not deactivate or set an expiry)',
      });
      if (error) console.warn('[general-intake] ensure token failed:', error.message);   // surfaced, not swallowed
    })();
  }, []);

  // States list for the parcel year-roll (loaded when the Year Management tab opens).
  useEffect(() => {
    if (activeTab !== 'years') return;
    supabase.rpc('parcel_states_summary').then(({ data }) => setRollStatesSummary(data || []));
  }, [activeTab]);

  function parseBulkCSV(text) {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    return lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
      return obj;
    }).filter(r => r.name);
  }

  function handleBulkPreview() {
    const rows = parseBulkCSV(bulkText);
    setBulkPreview(rows);
  }

  async function handleBulkImport() {
    if (!bulkPreview.length) return;
    setSaving(true);
    let success = 0, errors = 0;
    const contactErrors = [];
    for (const row of bulkPreview) {
      const id = row.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30);
      const { error } = await supabase.from('clients').insert({
        id, name: row.name, short_name: row.short_name || null,
        client_type: row.client_type || 'A Level', status: 'Active',
        address: row.address || null, notes: row.notes || null,
      });
      if (error) { errors++; continue; }
      success++;
      // The primary contact columns become the client screen's primary contact (utils/clientPrimaryContact).
      if (row.primary_contact_name || row.primary_contact_email || row.primary_contact_phone) {
        const pErr = await savePrimaryContact(id, null, { ...splitName(row.primary_contact_name), email: row.primary_contact_email, phone: row.primary_contact_phone });
        if (pErr) contactErrors.push(`${row.name}: ${pErr}`);
      }
    }
    setSaving(false);
    setSaveMsg(`Imported ${success} clients${errors ? `, ${errors} errors` : ''}${contactErrors.length ? ` — ${contactErrors.length} primary contact(s) not saved: ${contactErrors.join('; ')}` : ''}`);
    loadClients();
    setShowBulk(false);
    setBulkPreview([]);
    setBulkText('');
    setTimeout(() => setSaveMsg(''), 4000);
  }

  const filteredClients = clients.filter(c2 => !searchQ || c2.name.toLowerCase().includes(searchQ.toLowerCase()) || (c2.short_name && c2.short_name.toLowerCase().includes(searchQ.toLowerCase())));
  const filteredPortal = portalUsers.filter(u => !searchQ || u.name.toLowerCase().includes(searchQ.toLowerCase()) || u.email.toLowerCase().includes(searchQ.toLowerCase()));

  const tabs = [
    { id: 'clients', label: 'Client Management', icon: '🏦', count: clients.length },
    { id: 'portal', label: 'Portal Users', icon: '👥', count: portalUsers.length },
    { id: 'years', label: 'Year Management', icon: '📅', count: null },
  ];

  const generalIntakeUrl = `${window.location.origin}/upload/${GENERAL_INTAKE_TOKEN}`;
  const [copied, setCopied] = React.useState(false);

  function copyLink() {
    navigator.clipboard.writeText(generalIntakeUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={{ maxWidth: '1100px' }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text, marginBottom: '4px' }}>Admin Console</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '22px' }}>Manage clients, staff, and portal users</div>

      {/* Save message */}
      {saveMsg && (
        <div style={{ background: saveMsg.startsWith('Error') ? 'rgba(248,113,113,0.1)' : 'rgba(52,211,153,0.1)', border: `1px solid ${saveMsg.startsWith('Error') ? 'rgba(248,113,113,0.3)' : 'rgba(52,211,153,0.3)'}`, borderRadius: '8px', padding: '10px 16px', fontSize: '13px', color: saveMsg.startsWith('Error') ? '#f87171' : '#34d399', marginBottom: '14px' }}>
          {saveMsg.startsWith('Error') ? '✕ ' : '✓ '}{saveMsg}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${c.border}`, marginBottom: '20px' }}>
        {tabs.map(tab => (
          <div key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchQ(''); setShowBulk(false); }}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', cursor: 'pointer', borderBottom: activeTab === tab.id ? '2px solid #B40000' : '2px solid transparent', color: activeTab === tab.id ? 'var(--red)' : c.muted, fontSize: '13px', fontWeight: activeTab === tab.id ? '600' : '400', marginBottom: '-1px' }}>
            <span>{tab.icon}</span>{tab.label}
            <span style={{ ...s.badge('96,165,250'), fontSize: '10px', padding: '2px 7px' }}>{tab.count}</span>
          </div>
        ))}
      </div>

      {/* Search + actions */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <input style={{ ...s.input, flex: 1 }} placeholder={`Search ${activeTab}...`} value={searchQ} onChange={e => setSearchQ(e.target.value)} />
        {/* Creating a single client moved to Relationships > Onboarding. Bulk Import stays here
            until it and the single-client insert converge — see AddClientModal's header. */}
        {activeTab === 'clients' && (
          <button style={s.btnGhost} onClick={() => setShowBulk(!showBulk)}>📥 Bulk Import</button>
        )}
      </div>

      {/* NEW CLIENT FORM */}
      {/* General Intake Link */}
      <div style={{ background: isDark ? 'rgba(47,56,84,0.6)' : '#ffffff', border: '1px solid rgba(180,0,0,0.4)', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: c.text }}>🔗 General Intake Link</div>
            <div style={{ fontSize: '11px', color: c.muted, marginTop: '2px' }}>Permanent upload link — not tied to any client. Files land in Document Intake → General Inbox.</div>
          </div>
        </div>
        <div style={{ padding: '14px 18px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ flex: 1, padding: '8px 12px', background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--line-soft)', borderRadius: '7px', border: `1px solid ${c.border}`, fontSize: '12px', color: c.muted, fontFamily: "'DM Mono',monospace", wordBreak: 'break-all' }}>
            {generalIntakeUrl}
          </div>
          <button onClick={copyLink} style={{ padding: '8px 16px', background: copied ? 'rgba(52,211,153,0.15)' : 'linear-gradient(135deg,#B40000,#9C0000)', border: copied ? '1px solid #34d399' : 'none', borderRadius: '7px', color: copied ? '#34d399' : 'var(--ink)', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' }}>
            {copied ? '✓ Copied!' : '📋 Copy Link'}
          </button>
          <a href={generalIntakeUrl} target="_blank" rel="noopener noreferrer" style={{ padding: '8px 14px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '12px', fontWeight: '600', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            ↗ Open
          </a>
        </div>
      </div>

      {/* BULK IMPORT */}
      {showBulk && activeTab === 'clients' && (
        <div style={s.card}>
          <div style={s.cardHeader}>
            <div style={s.cardTitle}>📥 Bulk Import Clients</div>
            <button style={s.btnGhost} onClick={() => { setShowBulk(false); setBulkText(''); setBulkPreview([]); }}>✕ Close</button>
          </div>
          <div style={{ padding: '18px' }}>
            <div style={{ fontSize: '12px', color: c.muted, marginBottom: '10px', fontFamily: "'DM Mono',monospace" }}>
              Paste CSV with headers: name, short_name, client_type, primary_contact_name, primary_contact_email, primary_contact_phone, address, notes
            </div>
            <textarea style={{ ...s.textarea, minHeight: '120px', fontFamily: "'DM Mono',monospace", fontSize: '12px', marginBottom: '10px' }}
              placeholder={'name,short_name,client_type,primary_contact_name,primary_contact_email\n"Acme Corp","Acme","A Level","John Smith","john@acme.com"'}
              value={bulkText} onChange={e => setBulkText(e.target.value)} />
            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
              <button style={s.btn} onClick={handleBulkPreview}>Preview Import</button>
              {bulkPreview.length > 0 && <button style={{ ...s.btn, background: 'linear-gradient(135deg,#34d399,#059669)' }} onClick={handleBulkImport} disabled={saving}>{saving ? 'Importing...' : `Import ${bulkPreview.length} clients`}</button>}
            </div>
            {bulkPreview.length > 0 && (
              <div style={{ ...s.card, margin: 0 }}>
                <div style={s.cardHeader}><div style={s.cardTitle}>Preview — {bulkPreview.length} rows</div></div>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>Name</th><th style={s.th}>Short</th><th style={s.th}>Type</th><th style={s.th}>Contact</th><th style={s.th}>Email</th></tr></thead>
                  <tbody>
                    {bulkPreview.slice(0, 10).map((row, i) => (
                      <tr key={i}><td style={s.td}>{row.name}</td><td style={s.td}>{row.short_name}</td><td style={s.td}>{row.client_type}</td><td style={s.td}>{row.primary_contact_name}</td><td style={s.td}>{row.primary_contact_email}</td></tr>
                    ))}
                    {bulkPreview.length > 10 && <tr><td colSpan="5" style={{ ...s.td, color: c.muted, fontStyle: 'italic' }}>...and {bulkPreview.length - 10} more</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* EDIT CLIENT — the shared client form (AddClientModal in edit mode: fresh load, saves only changed fields). */}
      {editingClient && (
        <AddClientModal isDark={isDark} client={editingClient} onClose={() => setEditingClient(null)}
          onSuccess={() => { setEditingClient(null); loadClients(); setSaveMsg('Saved'); setTimeout(() => setSaveMsg(''), 3000); }} />
      )}

      {/* ── CLIENTS TABLE ── */}
      {activeTab === 'clients' && (
        <div style={s.card}>
          <div style={s.cardHeader}>
            <div style={s.cardTitle}>All Clients</div>
            <div style={{ fontSize: '12px', color: c.muted }}>{filteredClients.length} of {clients.length}</div>
          </div>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Client Name</th>
              <th style={s.th}>Short</th>
              <th style={s.th}>Type</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Primary Contact</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Phone</th>
              <th style={{ ...s.th, textAlign: 'center' }}>Action</th>
            </tr></thead>
            <tbody>
              {filteredClients.map(cl => (
                <tr key={cl.id}
                  onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={s.td}><div style={{ fontWeight: '600', color: c.text }}>{cl.name}</div><div style={{ fontSize: '11px', color: c.muted, fontFamily: 'monospace' }}>{cl.id}</div></td>
                  <td style={{ ...s.td, color: 'var(--red)', fontFamily: 'monospace', fontSize: '12px' }}>{cl.short_name || '—'}</td>
                  <td style={s.td}>{cl.client_type ? <span style={s.badge(cl.client_type === 'A Level' ? '180,0,0' : cl.client_type === 'B Level' ? '96,165,250' : '167,139,250')}>{cl.client_type}</span> : '—'}</td>
                  {/* ⛔ CASE-INSENSITIVE. clients.status is free text with no CHECK constraint, and an
                      exact match here painted a perfectly healthy client red for a lowercase 'a'. The
                      writer that produced it is fixed, but the column can still hold anything, so the
                      read no longer depends on casing. The raw stored value is still what is displayed. */}
                  <td style={s.td}><span style={s.badge(String(cl.status || 'Active').toLowerCase() === 'active' ? '52,211,153' : '248,113,113')}>{cl.status || 'Active'}</span></td>
                  <td style={{ ...s.td, fontSize: '12px', color: c.sub }}>{[primaryByClient.get(cl.id)?.first_name, primaryByClient.get(cl.id)?.last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td style={{ ...s.td, fontSize: '12px', color: c.sub }}>{primaryByClient.get(cl.id)?.email || '—'}</td>
                  <td style={{ ...s.td, fontSize: '12px', color: c.sub }}>{primaryByClient.get(cl.id)?.phone || '—'}</td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    <button style={s.btn} onClick={() => setEditingClient(cl)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── YEAR MANAGEMENT ── */}
      {activeTab === 'years' && (
        <div style={{ maxWidth: '600px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: '700', color: c.text, marginBottom: '4px' }}>Year Management</div>
          <div style={{ fontSize: '12px', color: c.muted, marginBottom: '20px' }}>Control which year subfolders are available across all document folders. Add a year at any time — the system auto-rolls each January 1.</div>

          {yearConfig && (
            <>
              <div style={s.card}>
                <div style={s.cardHeader}><div style={s.cardTitle}>Current Year Range</div></div>
                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '20px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'DM Mono',monospace", marginBottom: '4px' }}>From</div>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: c.text, fontFamily: "'Archivo',sans-serif" }}>{yearConfig.min_year}</div>
                    </div>
                    <div style={{ fontSize: '24px', color: c.muted }}>→</div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'DM Mono',monospace", marginBottom: '4px' }}>Through</div>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--red)', fontFamily: "'Archivo',sans-serif" }}>{yearConfig.current_max_year}</div>
                    </div>
                    <div style={{ flex: 1 }} />
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', color: c.muted, marginBottom: '4px' }}>Auto-roll</div>
                      <span style={s.badge(yearConfig.auto_roll ? '52,211,153' : '251,191,36')}>{yearConfig.auto_roll ? 'On — rolls Jan 1' : 'Off'}</span>
                    </div>
                  </div>

                  <div style={{ background: isDark ? 'rgba(0,0,0,0.2)' : '#f8f9fc', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '12px', color: c.muted }}>
                    All document folders with year subfolders currently show years <strong style={{ color: c.text }}>{yearConfig.min_year} through {yearConfig.current_max_year}</strong>. Adding a year will immediately make the new year available in all folder views.
                  </div>

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button style={s.btn} disabled={addingYear} onClick={async () => {
                      setAddingYear(true);
                      const newMax = yearConfig.current_max_year + 1;
                      const { error } = await supabase.from('folder_year_config')
                        .update({ current_max_year: newMax, updated_at: new Date().toISOString(), updated_by: staffUser?.name || 'Admin' })
                        .eq('id', yearConfig.id);
                      if (!error) {
                        setSaveMsg(`Year ${newMax} added successfully`);
                        loadYearConfig();
                        setTimeout(() => setSaveMsg(''), 3000);
                      } else {
                        setSaveMsg('Error: ' + error.message);
                      }
                      setAddingYear(false);
                    }}>
                      {addingYear ? 'Adding...' : `+ Add ${yearConfig.current_max_year + 1}`}
                    </button>
                    <span style={{ fontSize: '12px', color: c.muted }}>Adds {yearConfig.current_max_year + 1} to all year-subfolder folders immediately</span>
                  </div>
                </div>
              </div>

              <div style={s.card}>
                <div style={s.cardHeader}><div style={s.cardTitle}>All Years Active</div></div>
                <div style={{ padding: '14px 18px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {Array.from({ length: yearConfig.current_max_year - yearConfig.min_year + 1 }, (_, i) => yearConfig.min_year + i).map(yr => (
                    <span key={yr} style={{ ...s.badge(yr === yearConfig.current_max_year ? '180,0,0' : '96,165,250'), fontSize: '13px', padding: '5px 14px' }}>{yr}</span>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── ROLL PARCELS TO A NEW YEAR (state-scoped; independent of the Jan-1 folder auto-roll above) ── */}
          <div style={s.card}>
            <div style={s.cardHeader}><div style={s.cardTitle}>Roll Parcels to a New Year</div></div>
            <div style={{ padding: '18px 20px' }}>
              <div style={{ fontSize: '12px', color: c.muted, marginBottom: '16px', lineHeight: 1.5 }}>
                Creates the target year's parcel info for the selected states by carrying each parcel's most recent year forward (owner, assessor, collectors, services, etc.) as an editable starting point. Values start blank. Create-only — parcels that already have the target year are skipped.
              </div>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px' }}>Target year</div>
                <select value={rollTargetYear} onChange={e => { setRollTargetYear(e.target.value); setRollPreview(null); setRollResult(null); }} style={{ ...s.select, width: '140px' }}>
                  {['2023', '2024', '2025', '2026', '2027', '2028'].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div style={{ fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>States to roll <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(parcel count)</span></div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {rollStatesSummary.length === 0 ? (
                  <span style={{ fontSize: '12px', color: c.muted, fontStyle: 'italic' }}>Loading states…</span>
                ) : rollStatesSummary.map(st => {
                  const on = rollStates.has(st.state);
                  return (
                    <button key={st.state} type="button"
                      onClick={() => { setRollStates(prev => { const n = new Set(prev); if (n.has(st.state)) n.delete(st.state); else n.add(st.state); return n; }); setRollPreview(null); setRollResult(null); }}
                      title={`${st.parcel_count} parcels · current max year ${st.max_year || '—'}`}
                      style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid ' + (on ? '#B40000' : c.inputBorder), background: on ? 'rgba(180,0,0,0.12)' : 'transparent', color: on ? 'var(--red)' : c.sub, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
                      {st.state} <span style={{ opacity: 0.65 }}>({st.parcel_count})</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button style={{ ...s.btn, opacity: (rolling || rollStates.size === 0) ? 0.5 : 1 }} disabled={rolling || rollStates.size === 0}
                  onClick={async () => {
                    setRolling(true); setRollResult(null);
                    const { data, error } = await supabase.rpc('roll_state_parcels_preview', { p_target_year: rollTargetYear, p_states: [...rollStates] });
                    setRolling(false);
                    if (error) { setSaveMsg('Preview error: ' + error.message); return; }
                    setRollPreview(Array.isArray(data) ? data[0] : data);
                  }}>
                  {rolling ? 'Working…' : 'Preview'}
                </button>
                {rollStates.size === 0 && <span style={{ fontSize: '12px', color: c.muted }}>Select at least one state.</span>}
              </div>

              {rollPreview && !rollResult && (
                <div style={{ marginTop: '16px', padding: '14px 16px', background: isDark ? 'rgba(180,0,0,0.06)' : 'rgba(180,0,0,0.04)', border: `1px solid ${c.border}`, borderRadius: '8px' }}>
                  <div style={{ fontSize: '13px', color: c.text, marginBottom: '12px', lineHeight: 1.5 }}>
                    Roll <strong>{[...rollStates].join(', ')}</strong> to <strong>{rollTargetYear}</strong>? <strong style={{ color: 'var(--red)' }}>{rollPreview.parcels_to_create}</strong> parcels will be created, <strong>{rollPreview.parcels_to_skip}</strong> already have {rollTargetYear} data (skipped), <strong>{rollPreview.total_in_states}</strong> total in selected states.
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={s.btnGhost} disabled={rolling} onClick={() => setRollPreview(null)}>Cancel</button>
                    <button style={{ ...s.btn, opacity: (rolling || rollPreview.parcels_to_create === 0) ? 0.5 : 1 }} disabled={rolling || rollPreview.parcels_to_create === 0}
                      onClick={async () => {
                        setRolling(true);
                        const { data, error } = await supabase.rpc('roll_state_parcels', { p_target_year: rollTargetYear, p_states: [...rollStates] });
                        setRolling(false);
                        if (error) { setSaveMsg('Roll error: ' + error.message); return; }
                        setRollResult(Array.isArray(data) ? data[0] : data); setRollPreview(null);
                        supabase.rpc('parcel_states_summary').then(({ data: d }) => setRollStatesSummary(d || [])); // refresh max years
                      }}>
                      {rolling ? 'Rolling…' : `Confirm — roll ${rollPreview.parcels_to_create}`}
                    </button>
                  </div>
                </div>
              )}

              {rollResult && (
                <div style={{ marginTop: '16px', padding: '14px 16px', background: isDark ? 'rgba(52,211,153,0.06)' : 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.35)', borderRadius: '8px', fontSize: '13px', color: c.text }}>
                  ✓ Rolled to {rollTargetYear}: <strong>{rollResult.parcels_created}</strong> parcels created, <strong>{rollResult.parcels_skipped}</strong> skipped, <strong>{rollResult.collector_rows_created}</strong> collector rows copied.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── PORTAL USERS TABLE ── */}
      {activeTab === 'portal' && (
        <div style={s.card}>
          <div style={s.cardHeader}>
            <div style={s.cardTitle}>Client Portal Users</div>
            <div style={{ fontSize: '12px', color: c.muted }}>{filteredPortal.length} users</div>
          </div>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Name</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Title</th>
              <th style={s.th}>Client</th>
              <th style={s.th}>Auth Status</th>
            </tr></thead>
            <tbody>
              {filteredPortal.map(u => (
                <tr key={u.id}
                  onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={s.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg,#B40000,#9C0000)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: 'white', flexShrink: 0 }}>
                        {u.name ? u.name.split(' ').map(w => w[0]).join('').slice(0, 2) : '?'}
                      </div>
                      <div style={{ fontWeight: '600', color: c.text }}>{u.name}</div>
                    </div>
                  </td>
                  <td style={{ ...s.td, fontSize: '12px', color: c.sub }}>{u.email}</td>
                  <td style={{ ...s.td, fontSize: '12px', color: c.sub }}>{u.title || '—'}</td>
                  <td style={{ ...s.td, fontSize: '12px', color: 'var(--red)' }}>{u.clients?.name || u.client_id}</td>
                  <td style={s.td}><span style={s.badge(u.auth_user_id ? '52,211,153' : '251,191,36')}>{u.auth_user_id ? 'Active' : 'Pending'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
